// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState } from 'react'
import { FileSignature, Loader2 } from 'lucide-react'
import SidePanel from '../../components/common/SidePanel'
import EmailPicker, { type EmailChoice } from './EmailPicker'
import { allowsDocuSign, SIGNING_WORDS, type SigningMode } from '../../api/inappEsign'
import { DocuSignNotSetUpNote, DocuSignOffNote } from '../../components/carerx/SigningNotes'

/**
 * UX audit 2026-10-04 (P0 B4): "Send for signature" on a Plan of Care posted a hard-coded
 * physician@example.com and then said "Sent for signature." — the real prescriber never got it.
 * The operator now chooses the recipient: the plan's prescriber's PioneerRx / saved emails, or a typed
 * one, and the name that goes on the envelope. Nothing is sent without both.
 */
export function canSendPlanOfCare(name: string, email: string): boolean {
  return name.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
}

export default function PlanOfCareSendDialog({ open, prescriberId, prescriberName, busy, error, mode = 'docusign', docusignReady = true, onCancel, onSend }: {
  open: boolean
  /** D-CRX-87 §4d: the Plan of Care is DocuSign only. Under in_app it cannot be sent (note instead of Send). */
  mode?: SigningMode
  /** DocuSign is set up for this pharmacy; when false, Send is disabled and says why. */
  docusignReady?: boolean
  prescriberId?: string | null
  prescriberName?: string | null
  busy: boolean
  /** The send's failure, shown here — the page behind the panel is covered by it. */
  error?: string | null
  onCancel: () => void
  onSend: (recipient: { recipient_name: string; recipient_email: string }, saveToProfile: boolean) => void
}) {
  const [name, setName] = useState(prescriberName ?? '')
  const [choice, setChoice] = useState<EmailChoice>({ email: '', saveToProfile: false })
  const docuSign = allowsDocuSign(mode)
  const ok = canSendPlanOfCare(name, choice.email) && docuSign && docusignReady
  const lbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-soft)', marginTop: 12 }
  const inp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13.5, background: 'var(--color-surface)', color: 'var(--color-text)' }
  return (
    <SidePanel open={open} title="Send Plan of Care for signature" width={480} onClose={onCancel}>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)' }}>
        The prescriber signs the Plan of Care through DocuSign. Check who receives it before sending.
      </div>
      <label style={lbl}>Prescriber name (shown on the envelope)
        <input value={name} onChange={e => setName(e.target.value)} style={inp} placeholder="Dr. Jane Smith" />
      </label>
      <div style={lbl}>Prescriber — email</div>
      <EmailPicker subjectType="prescriber" subjectId={prescriberId} roleLabel="provider" onChange={setChoice} />
      {!prescriberId && (
        <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>
          This plan has no prescriber on file — type the email the prescriber signs with.
        </div>
      )}
      {!docuSign && <DocuSignOffNote />}
      {docuSign && !docusignReady && <DocuSignNotSetUpNote />}
      {error && <div role="alert" style={{ fontSize: 12.5, color: '#7f1d1d', background: '#fef2f2', border: '1px solid #f0a8a8', borderRadius: 8, padding: '8px 11px', marginTop: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
        <button type="button" onClick={onCancel}
          style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
        {docuSign && <button type="button" disabled={!ok || busy} title={docusignReady ? undefined : SIGNING_WORDS.docusignNotSetUp}
          onClick={() => onSend({ recipient_name: name.trim(), recipient_email: choice.email.trim() }, choice.saveToProfile)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, opacity: ok && !busy ? 1 : .5, cursor: ok && !busy ? 'pointer' : 'not-allowed' }}>
          {busy ? <Loader2 className="spin" size={13} /> : <FileSignature size={13} />} Send
        </button>}
      </div>
    </SidePanel>
  )
}
